import { useState, useEffect } from "react";
import { X, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

/**
 * Confirm-and-delete modal.
 * Calls /delete-preview on open to show what's linked.
 * Refuses delete if unsafe unless admin ticks "force".
 */
export function DeleteCustomerModal({ customer, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [force, setForce] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/customers/${customer.customer_id}/delete-preview`, { headers: headers() });
        setPreview(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not load delete preview");
        onClose?.();
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.customer_id]);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const params = (!preview.safe_to_delete && force) ? "?force=true" : "";
      const r = await axios.delete(`${API}/admin/customers/${customer.customer_id}${params}`, { headers: headers() });
      toast.success(r.data.force_used ? "Customer force-deleted" : "Customer permanently deleted");
      onDeleted?.(customer.customer_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.blockers) {
        // Refresh preview with the latest blockers from the server
        setPreview({
          ...preview,
          safe_to_delete: false,
          blockers: detail.blockers,
          linked: detail.linked,
        });
        toast.error(detail.message || "Cannot delete — see blockers");
      } else {
        toast.error(detail || "Delete failed");
      }
    }
    setDeleting(false);
  };

  const safeFlow = preview?.safe_to_delete;
  const expectedConfirm = (customer.email || customer.name || "").toLowerCase();
  const confirmOk = confirmText.trim().toLowerCase() === expectedConfirm;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="delete-customer-modal">
      <div className="bg-white max-w-md w-full p-6 border border-red-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-red-50 border border-red-200 flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="text-[18px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              Permanently delete customer
            </h3>
          </div>
          <button onClick={onClose} className="p-1 text-[#676664]/40 hover:text-[#282828]" data-testid="close-delete-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="text-[12px] text-[#676664] mb-4">
          <strong className="text-[#282828]">{customer.name || "(no name)"}</strong> · {customer.email}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" />
          </div>
        )}

        {!loading && preview && (
          <>
            {safeFlow ? (
              <div className="bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 mb-4" data-testid="safe-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-0.5">This action cannot be undone</div>
                    <div className="text-[11px] text-amber-700">No bookings, payments, or appointments are linked. The customer record will be permanently removed.</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 p-3 text-[12px] text-red-900 mb-4" data-testid="unsafe-blocker">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">This customer has booking/payment history</div>
                    <div className="text-[11px] text-red-700">Permanent deletion is blocked because of the following linked records:</div>
                  </div>
                </div>
                <ul className="list-disc list-inside text-[11px] text-red-800 space-y-0.5 ml-1">
                  {(preview.blockers || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                <p className="text-[11px] text-red-700 mt-2 italic">We recommend archiving instead.</p>

                <label className="flex items-start gap-2 mt-3 pt-3 border-t border-red-200 text-[11px] text-red-900">
                  <input
                    type="checkbox"
                    checked={force}
                    onChange={(e) => setForce(e.target.checked)}
                    className="mt-0.5"
                    data-testid="force-delete-checkbox"
                  />
                  <span>
                    I understand this will orphan the linked records.
                    Force-delete anyway (use with caution — usually only for test customers).
                  </span>
                </label>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-wider text-[#676664]/60 mb-1">
                Type <span className="font-mono text-[#282828]">{expectedConfirm}</span> to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full border border-[#282828]/15 px-3 py-2 text-[13px] focus:border-red-400 focus:outline-none"
                placeholder={expectedConfirm}
                autoFocus
                data-testid="confirm-text-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} className="border-[#282828]/10" data-testid="cancel-delete-btn">
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting || !confirmOk || (!safeFlow && !force)}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                data-testid="confirm-delete-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {safeFlow ? "Permanently delete" : "Force delete"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
